/**
 * Filtry listy w adresie (`?publisher=12&publisher=15&discipline=7`).
 *
 * Dzięki temu po wejściu w pozycję i powrocie przyciskiem „wstecz” lista odtwarza zaznaczone filtry,
 * a link z filtrami da się wysłać dalej. Nazwą parametru jest `id` filtru ze schematu.
 */
const asArray = (value) => (Array.isArray(value) ? value : [])

const MULTI_CONTROLS = new Set(['multi', 'searchable-multi', 'pill-multi', 'select-multi'])

export const collectFilterDescriptors = (schema, excludedIds = null) => {
  const excluded = new Set(asArray(excludedIds))
  const descriptors = []

  const collect = (item) => {
    if (!item?.id || !item.control) return

    if (!excluded.has(item.id)) {
      descriptors.push(item)
    }

    asArray(item.dependentFields).forEach(collect)
  }

  asArray(schema)
    .filter((item) => item?.type === 'filter')
    .forEach(collect)

  return descriptors
}

// Adres niesie tekst, a opcje filtrów mają wartości z API (np. liczbowe id) — wracamy do oryginalnego typu.
const resolveOptionValue = (descriptor, optionsById, rawValue) => {
  const options = asArray(descriptor.options).length > 0 ? descriptor.options : optionsById?.[descriptor.optionsKey]
  const option = asArray(options).find((item) => String(item?.value) === rawValue)

  return option ? option.value : rawValue
}

/**
 * Stan filtrów zapisany w adresie. `present` mówi, które filtry w ogóle były w adresie —
 * pozostałe dostają wartości z `defaults`.
 */
export const readFiltersFromSearch = (search, descriptors, { optionsById, defaults }) => {
  const params = new URLSearchParams(search || '')
  const state = {
    textValues: { ...defaults.textValues },
    multiValues: { ...defaults.multiValues },
    singleValues: { ...defaults.singleValues },
    booleanValues: { ...defaults.booleanValues },
  }
  const present = new Set()

  for (const descriptor of descriptors) {
    const { id, control } = descriptor

    if (!params.has(id)) continue

    present.add(id)

    if (MULTI_CONTROLS.has(control)) {
      state.multiValues[id] = new Set(
        params
          .getAll(id)
          .filter(Boolean)
          .map((value) => resolveOptionValue(descriptor, optionsById, value)),
      )
    } else if (control === 'text') {
      state.textValues[id] = params.get(id) || ''
    } else if (control === 'single') {
      state.singleValues[id] = resolveOptionValue(descriptor, optionsById, params.get(id) || '')
    } else if (control === 'toggle') {
      state.booleanValues[id] = params.get(id) === '1'
    }
  }

  return { state, present }
}

// Zapisuje do `params` filtry różne od domyślnych, a domyślne usuwa z adresu.
export const writeFiltersToParams = (params, descriptors, state, defaults) => {
  for (const { id, control } of descriptors) {
    params.delete(id)

    if (MULTI_CONTROLS.has(control)) {
      Array.from(state.multiValues?.[id] || []).forEach((value) => params.append(id, String(value)))
    } else if (control === 'text') {
      const value = state.textValues?.[id] ?? ''

      if (value !== '' && value !== defaults.textValues?.[id]) params.set(id, value)
    } else if (control === 'single') {
      const value = state.singleValues?.[id] ?? ''

      if (value !== '' && value !== defaults.singleValues?.[id]) params.set(id, String(value))
    } else if (control === 'toggle') {
      if (Boolean(state.booleanValues?.[id]) !== Boolean(defaults.booleanValues?.[id])) {
        params.set(id, state.booleanValues?.[id] ? '1' : '0')
      }
    }
  }

  return params
}
